import React, { useState, useMemo } from 'react';
import { Card, Typography, Box, LinearProgress, Chip, Stack, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert, Button, Pagination } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import checkCircle from '@iconify/icons-ant-design/check-circle';
import closeCircle from '@iconify/icons-ant-design/close-circle';
import exclamationCircle from '@iconify/icons-ant-design/exclamation-circle';
import schedule from '@iconify/icons-ant-design/schedule';
import rise from '@iconify/icons-ant-design/rise';
import refresh from '@iconify/icons-ant-design/reload';
import { BaseChart } from 'src/components/charts/BaseOptionChart';
import useStationUptime from 'src/hooks/useStationUptime.hook';
import { useSelectedTeam } from 'src/hooks/useSelectedTeam';
import { isSameDay } from 'date-fns';

// ----------------------------------------------------------------------

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.secondary.light}40`,
  background: `linear-gradient(135deg, ${theme.palette.secondary.lighter}08 0%, ${theme.palette.secondary.light}05 100%)`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2.5),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3),
  },
}));

const UptimeCard = styled(Box)(({ theme, status }) => {
  const getStatusColor = () => {
    if (status >= 95) return theme.palette.success.main;
    if (status >= 90) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const getStatusLightColor = () => {
    if (status >= 95) return theme.palette.success.light;
    if (status >= 90) return theme.palette.warning.light;
    return theme.palette.error.light;
  };

  const getStatusLighterColor = () => {
    if (status >= 95) return theme.palette.success.lighter;
    if (status >= 90) return theme.palette.warning.lighter;
    return theme.palette.error.lighter;
  };

  return {
    padding: theme.spacing(2.5),
    borderRadius: theme.shape.borderRadius * 1.5,
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${getStatusLighterColor()}15 50%, ${getStatusLightColor()}08 100%)`,
    border: `1px solid ${getStatusLightColor()}30`,
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 25px ${getStatusLightColor()}20`,
      border: `1px solid ${getStatusLightColor()}50`,
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: `linear-gradient(90deg, ${getStatusColor()} 0%, ${getStatusLightColor()} 100%)`,
      borderRadius: `${theme.shape.borderRadius * 1.5}px ${theme.shape.borderRadius * 1.5}px 0 0`,
    },
  };
});

const StationUptimeDashboard = ({ timeRange = 'Last7Days' }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [chartKey, setChartKey] = useState(0);
  const stationsPerPage = 5;
  const { teamId } = useSelectedTeam();

  // Convert dashboard time period to chart format
  const getChartTimeRange = (dashboardTimeRange) => {
    // Handle both string and object formats
    const timeRangeType = typeof dashboardTimeRange === 'object' ? dashboardTimeRange.type : dashboardTimeRange;
    
    switch (timeRangeType) {
      case 'Today':
        return '1d';
      case 'Last7Days':
        return '7d';
      case 'Last30Days':
        return '30d';
      case 'Last90Days':
        return '90d';
      default:
        return '7d';
    }
  };

  const chartTimeRange = getChartTimeRange(timeRange);
  const timeRangeType = typeof timeRange === 'object' ? timeRange.type : timeRange;
  
  const {
    data,
    isLoading,
    error,
    refresh
  } = useStationUptime(teamId, chartTimeRange);

  // Handle time period change (now triggered by parent)
  React.useEffect(() => {
    setCurrentPage(1); // Reset to first page when changing time period
    setChartKey(prev => prev + 1); // Force chart re-render
  }, [timeRange]);

  // Reset to first page when stations data changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [data?.stations]);

  // Show loading state when team is changing or data is loading
  if (isLoading || !data) {
    return (
      <StyledCard>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <Stack spacing={2} alignItems="center">
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              Loading Station Uptime Data...
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 300 }}>
              Please wait while we load the station uptime information.
            </Typography>
          </Stack>
        </Box>
      </StyledCard>
    );
  }

  // Extract data from API response
  const { summary, stations, historicalTrend } = data || {};
  const rawHistoricalData = historicalTrend?.uptimeData || [];
  const rawLabels = historicalTrend?.labels || [];
  
  // Remove only the last date if it's in the future
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  let labels = rawLabels;
  let historicalData = rawHistoricalData.map(value => {
    const numValue = parseFloat(value);
    return isNaN(numValue) ? 0 : numValue;
  });

  // Check only the last date if labels exist
  if (labels.length > 0 && historicalData.length > 0) {
    const lastLabel = labels[labels.length - 1];
    let shouldRemove = false;
    
    // First, check if the label matches tomorrow's date format directly
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowMonth = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][tomorrow.getMonth()];
    const tomorrowDay = tomorrow.getDate();
    const tomorrowLabel = `${tomorrowMonth} ${tomorrowDay}`;
    
    // Check if last label matches tomorrow's date (handles "Jan 15" format)
    if (lastLabel.trim() === tomorrowLabel || lastLabel.includes(tomorrowLabel)) {
      shouldRemove = true;
    } else {
      // Try to parse the date and check if it's in the future
      let lastDate = null;
      
      // Method 1: Parse from format like "Jan 15" (most common format from API)
      const dateMatch = lastLabel.match(/(\w+)\s+(\d+)/);
      if (dateMatch) {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthIndex = monthNames.indexOf(dateMatch[1]);
        if (monthIndex !== -1) {
          const currentYear = new Date().getFullYear();
          const day = parseInt(dateMatch[2]);
          
          // Try current year first
          lastDate = new Date(currentYear, monthIndex, day);
          lastDate.setHours(0, 0, 0, 0);
          
          // If it's in the past (more than 30 days), try next year
          const daysDiff = (today.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24);
          if (daysDiff > 30) {
            lastDate = new Date(currentYear + 1, monthIndex, day);
            lastDate.setHours(0, 0, 0, 0);
          }
          
          // Check if it's in the future
          if (lastDate > today) {
            shouldRemove = true;
          }
        }
      }
      
      // Method 2: Try direct date parsing
      if (!shouldRemove) {
        try {
          lastDate = new Date(lastLabel);
          if (!isNaN(lastDate.getTime())) {
            lastDate.setHours(0, 0, 0, 0);
            if (lastDate > today) {
              shouldRemove = true;
            }
          }
        } catch (e) {
          // Continue
        }
      }
      
      // Method 3: Try ISO format
      if (!shouldRemove) {
        const isoMatch = lastLabel.match(/(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
          lastDate = new Date(parseInt(isoMatch[1]), parseInt(isoMatch[2]) - 1, parseInt(isoMatch[3]));
          lastDate.setHours(0, 0, 0, 0);
          if (lastDate > today) {
            shouldRemove = true;
          }
        }
      }
    }
    
    // Remove the last date if it's in the future
    if (shouldRemove) {
      labels = labels.slice(0, -1);
      historicalData = historicalData.slice(0, -1);
    }
  }
  
  //   labelsLength: labels.length,
  //   timeRange
  // });

  // Calculate metrics from API data
  const overallUptime = summary?.overallUptime || 0;
  const excellentStations = summary?.excellentStations || 0;
  const needsAttentionStations = summary?.needsAttentionStations || 0;

  // Pagination logic
  const totalPages = Math.ceil((stations?.length || 0) / stationsPerPage);
  const startIndex = (currentPage - 1) * stationsPerPage;
  const endIndex = startIndex + stationsPerPage;
  const currentStations = stations?.slice(startIndex, endIndex) || [];

  // Handle page change
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };


  const getStatusIcon = (status) => {
    switch (status) {
      case 'excellent':
        return <Icon icon={checkCircle} width={16} height={16} />;
      case 'good':
        return <Icon icon={rise} width={16} height={16} />;
      case 'needs_attention':
        return <Icon icon={exclamationCircle} width={16} height={16} />;
      default:
        return <Icon icon={closeCircle} width={16} height={16} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent':
        return 'success';
      case 'good':
        return 'info';
      case 'needs_attention':
        return 'warning';
      default:
        return 'error';
    }
  };

  // Handle loading state
  if (isLoading) {
    return (
      <StyledCard>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      </StyledCard>
    );
  }

  // Handle error state
  if (error) {
    return (
      <StyledCard>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={refresh}>
              <Icon icon={refresh} width={16} height={16} style={{ marginRight: 4 }} />
              Retry
            </Button>
          }
        >
          Failed to load station uptime data: {error.message}
        </Alert>
      </StyledCard>
    );
  }

  // Handle no data state
  if (!data || !stations || stations.length === 0) {
    return (
      <StyledCard>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <Stack spacing={2} alignItems="center">
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              No Station Uptime Data Available
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 300 }}>
              Station uptime data will appear here once stations are operational and data is collected.
            </Typography>
            <Button variant="outlined" onClick={refresh} startIcon={<Icon icon={refresh} width={16} height={16} />}>
              Refresh Data
            </Button>
          </Stack>
        </Box>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
          Station Uptime Metrics
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Monitor station reliability and performance
        </Typography>
      </Box>

      {/* Overall Metrics */}
      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 1 }}>
        <Grid item xs={12} sm={4}>
          <UptimeCard status={overallUptime}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                color: overallUptime >= 95 ? 'success.main' : overallUptime >= 90 ? 'warning.main' : 'error.main',
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
              }}
            >
              {overallUptime.toFixed(1)}%
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' }
              }}
            >
              Overall Uptime
            </Typography>
          </UptimeCard>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box sx={{ 
            p: { xs: 1.5, sm: 2 }, 
            textAlign: 'center', 
            bgcolor: 'success.lighter', 
            borderRadius: 2 
          }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                color: 'success.main',
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
              }}
            >
              {excellentStations}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'success.dark',
                fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' }
              }}
            >
              Excellent Stations
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box sx={{ 
            p: { xs: 1.5, sm: 2 }, 
            textAlign: 'center', 
            bgcolor: needsAttentionStations > 0 ? 'warning.lighter' : 'success.lighter', 
            borderRadius: 2 
          }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                color: needsAttentionStations > 0 ? 'warning.main' : 'success.main',
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
              }}
            >
              {needsAttentionStations}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: needsAttentionStations > 0 ? 'warning.dark' : 'success.dark',
                fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem' }
              }}
            >
              Need Attention
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Historical Uptime Trend Chart */}
      {isLoading ? (
        <Box sx={{ mb: 1, p: 3, textAlign: 'center' }}>
          <CircularProgress size={40} />
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
            Loading uptime data for {timeRangeType === 'Today' ? 'Today' : timeRangeType === 'Last7Days' ? '7 Days' : timeRangeType === 'Last30Days' ? '30 Days' : timeRangeType === 'Last90Days' ? '90 Days' : '7 Days'}...
          </Typography>
        </Box>
      ) : historicalData.length > 0 ? (
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
            Uptime Trend ({timeRangeType === 'Today' ? 'Today' : timeRangeType === 'Last7Days' ? '7 Days' : timeRangeType === 'Last30Days' ? '30 Days' : timeRangeType === 'Last90Days' ? '90 Days' : '7 Days'})
          </Typography>
          <Box sx={{ 
            height: { xs: 250, sm: 300, md: 350 },
            overflow: 'hidden',
            width: '100%'
          }}>
            <BaseChart
              key={`uptime-chart-${timeRangeType}-${chartKey}`}
              series={[
                {
                  name: 'Overall Uptime',
                  data: historicalData,
                  color: '#3182CE',
                }
              ]}
              options={{
                chart: {
                  type: 'line',
                  height: '100%',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  toolbar: {
                    show: false,
                  },
                  background: 'transparent',
                  zoom: { enabled: false },
                  pan: { enabled: false },
                  selection: { enabled: false },
                  brush: { enabled: false },
                  animations: { enabled: false }
                },
                stroke: {
                  curve: 'smooth',
                  width: 3,
                },
                markers: {
                  size: 4,
                  strokeWidth: 2,
                  hover: {
                    size: 6,
                  },
                },
                xaxis: {
                  categories: labels,
                  title: {
                    text: 'Date',
                    style: {
                      fontSize: '12px',
                      fontWeight: 500,
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      color: '#718096'
                    },
                  },
                  labels: {
                    style: {
                      fontSize: '11px',
                      fontWeight: 400,
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      colors: '#4A5568'
                    }
                  }
                },
                yaxis: {
                  title: {
                    text: 'Uptime (%)',
                    style: {
                      fontSize: '12px',
                      fontWeight: 500,
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      color: '#718096'
                    },
                  },
                  labels: {
                    style: {
                      fontSize: '11px',
                      fontWeight: 400,
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      colors: '#4A5568'
                    },
                    formatter: (value) => `${value.toFixed(1)}%`
                  },
                  min: 0,
                  max: 100
                },
                grid: {
                  show: true,
                  borderColor: '#E2E8F0',
                  strokeDashArray: 3,
                  xaxis: {
                    lines: {
                      show: false
                    }
                  },
                  yaxis: {
                    lines: {
                      show: true
                    }
                  }
                },
                tooltip: {
                  style: {
                    fontSize: '12px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                  },
                  y: {
                    formatter: (value) => `${value.toFixed(1)}%`
                  },
                  followCursor: true,
                  offsetY: -10,
                  offsetX: 0,
                  fixed: {
                    enabled: false
                  },
                  position: 'top',
                  custom: function({ series, seriesIndex, dataPointIndex, w }) {
                    const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
                    const numValue = parseFloat(data);
                    const value = !isNaN(numValue) ? `${numValue.toFixed(1)}%` : '0.0%';
                    const seriesName = w.globals.initialSeries[seriesIndex].name || 'Uptime';
                    return `
                      <div style="
                        background: rgba(0, 0, 0, 0.8);
                        color: white;
                        padding: 8px 12px;
                        border-radius: 6px;
                        font-size: 12px;
                        font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                        max-width: 200px;
                        word-wrap: break-word;
                        z-index: 1000;
                      ">
                        <div style="font-weight: 600; margin-bottom: 4px;">${seriesName}</div>
                        <div>${value}</div>
                      </div>
                    `;
                  }
                },
                dataLabels: {
                  enabled: false
                }
              }}
              type="line"
              height={300}
            />
          </Box>
        </Box>
      ) : (
        <Box sx={{ mb: 1, p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No historical uptime data available for {timeRange === 'Today' ? 'Today' : timeRange === 'Last7Days' ? '7 Days' : timeRange === 'Last30Days' ? '30 Days' : timeRange === 'Last90Days' ? '90 Days' : '7 Days'}
          </Typography>
        </Box>
      )}

      {/* Station Details Table */}
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Station Performance
        </Typography>
        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'grey.200' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Station</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Uptime</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                {/* <TableCell sx={{ fontWeight: 600 }}>Last Issue</TableCell> */}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentStations.map((station) => (
                <TableRow key={station.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {station.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {station.location?.city || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={station.uptime}
                        sx={{
                          flex: 1,
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: station.uptime >= 95 ? 'success.main' : station.uptime >= 90 ? 'warning.main' : 'error.main',
                          },
                        }}
                      />
                      <Typography variant="body2" sx={{ minWidth: 40, fontWeight: 500 }}>
                        {station.uptime}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(station.status)}
                      label={station.status.replace('_', ' ').toUpperCase()}
                      color={getStatusColor(station.status)}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  {/* <TableCell>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {station.lastIssue}
                    </Typography>
                  </TableCell> */}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="medium"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  fontWeight: 600,
                },
              }}
            />
          </Box>
        )}
      </Box>
    </StyledCard>
  );
};

export default StationUptimeDashboard;
